import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Boxes, Upload, Trash2, Calendar, FileSpreadsheet, Loader2, Info, AlertTriangle, Euro, X, CheckCircle2 } from "lucide-react";

const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES");
const fmtEur = (v) => `${Number(v || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDay = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function StockPage() {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/stock/latest");
      setMeta(r.data);
      if (r.data?.has_expiry) {
        const e = await api.get("/stock/expiring?days=180");
        setExpiring(e.data);
      } else {
        setExpiring(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar el stock");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/stock/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      setTimeout(() => {
        document.querySelector("[data-testid=stock-preview]")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al leer el fichero");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmUpload = async () => {
    if (!preview) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("token", preview.token);
    try {
      const r = await api.post("/stock/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Stock guardado: ${r.data.total_lines} CN · ${fmtNum(r.data.total_units)} unidades`);
      setPreview(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar el stock");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("¿Eliminar el stock cargado? Tendrás que volver a subirlo para verlo en los análisis.")) return;
    try {
      await api.delete("/stock");
      toast.success("Stock eliminado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]" data-testid="stock-page">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Inventario · Voluntario</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950 inline-flex items-center gap-3">
          <Boxes className="h-9 w-9 text-slate-700" strokeWidth={1.5} />
          Stock de farmacia
        </h1>
        <p className="text-slate-500 mt-2 max-w-2xl text-sm">
          Sube un archivo (Excel, CSV, TXT o PDF) con tu stock actual: solo necesita una columna de <strong>CN</strong> y otra de <strong>unidades</strong>. Si además trae <strong>PVP, PVL o caducidad</strong>, los aprovechamos para valorar el stock con tu precio real y avisarte de lo que caduca.
          El stock se guarda con la fecha de subida y aparece como referencia en los análisis y los pedidos.
        </p>
      </header>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Estado actual</div>
          {meta?.has_stock && (
            <button
              onClick={handleDelete}
              data-testid="delete-stock"
              className="inline-flex items-center gap-1.5 text-xs text-rose-700 hover:text-rose-900 hover:bg-rose-50 px-3 py-1.5 rounded-md"
            >
              <Trash2 className="h-3.5 w-3.5" /> Eliminar stock
            </button>
          )}
        </div>
        {loading ? (
          <div className="text-slate-400 text-sm inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
        ) : meta?.has_stock ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="stock-meta">
            <KpiTile label="Subido el" value={fmtDate(meta.uploaded_at)} Icon={Calendar} />
            <KpiTile label="Referencias (CN)" value={fmtNum(meta.total_lines)} />
            <KpiTile label="Unidades totales" value={fmtNum(meta.total_units)} accent="emerald" />
            <div className="md:col-span-3 text-xs text-slate-500 flex items-center gap-2 mt-1 flex-wrap">
              <FileSpreadsheet className="h-3.5 w-3.5" /> Archivo: <strong className="text-slate-700">{meta.filename}</strong>
              {meta.detected_software && (
                <span
                  data-testid="detected-software-badge"
                  className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider"
                  title="Software de gestión farmacéutica detectado automáticamente a partir de las columnas del archivo"
                >
                  Detectado · {meta.detected_software}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="text-slate-500 text-sm bg-slate-50 border border-dashed border-slate-300 rounded-lg p-6 text-center">
            <Boxes className="h-8 w-8 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
            Aún no has subido stock. Subir el archivo es opcional pero te permite ver qué referencias tienes ya antes de hacer pedidos.
          </div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Subir o reemplazar</div>
        <p className="text-sm text-slate-600 mb-4">
          Antes de guardar verás una vista previa con lo que se ha leído. Al confirmar, el nuevo fichero reemplaza al stock anterior.
        </p>
        <label
          htmlFor="stock-file"
          className={`inline-flex items-center gap-2 bg-slate-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Subiendo…" : "Seleccionar archivo"}
        </label>
        <input
          ref={fileRef}
          id="stock-file"
          type="file"
          accept=".xlsx,.xls,.xlsm,.ods,.csv,.txt,.pdf"
          className="hidden"
          onChange={handleUpload}
          data-testid="stock-upload-input"
        />
      </section>

      {preview && (
        <section className="bg-white border-2 border-slate-900 rounded-2xl p-6 mb-6" data-testid="stock-preview">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Vista previa · aún no se ha guardado</div>
              <div className="font-display text-xl font-bold text-slate-950 mt-1">{preview.filename}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {preview.detected_software ? `Detectado: ${preview.detected_software} · ` : ""}
                Origen: {preview.source === "pdf" ? "PDF" : preview.source === "csv" ? "CSV/TXT" : "Excel"}
                {preview.rows_skipped > 0 && ` · ${fmtNum(preview.rows_skipped)} ${preview.rows_skipped === 1 ? "línea descartada" : "líneas descartadas"} (códigos de barras o sin CN)`}
              </div>
            </div>
            <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-900" data-testid="stock-preview-cancel">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Referencias leídas" value={fmtNum(preview.total_lines)} />
            <KpiTile label="Unidades" value={fmtNum(preview.total_units)} accent="emerald" />
            <KpiTile label="Casan con el catálogo" value={fmtNum(preview.in_catalog)} />
            <KpiTile label="Sin ficha en catálogo" value={fmtNum(preview.unknown_count)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-xs">
            <Flag ok={preview.has_pvl} label="PVL en el fichero" extra={preview.has_pvl ? fmtEur(preview.value_pvl) : "se usará el del catálogo"} />
            <Flag ok={preview.has_pvp} label="PVP en el fichero" extra={preview.has_pvp ? fmtEur(preview.value_pvp) : "no viene"} />
            <Flag ok={preview.has_expiry} label="Caducidades" extra={preview.has_expiry ? `${preview.expiring_count} caducan en 6 meses` : "no vienen"} />
          </div>

          {preview.unknown_count > 0 && (
            <details className="mt-4" data-testid="stock-preview-unknown">
              <summary className="text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-900">
                {preview.unknown_count === 1
                  ? "Ver la referencia que no está en el catálogo"
                  : `Ver las ${preview.unknown_count} referencias que no están en el catálogo`}
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                {preview.unknown_sample.map((u) => (
                  <div key={u.cn} className="px-3 py-1.5 flex justify-between text-xs">
                    <span className="font-mono-num text-slate-600">{u.cn}</span>
                    <span className="text-slate-500">{u.units} uds{u.expiry ? ` · cad. ${fmtDay(u.expiry)}` : ""}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {preview.previous && (
            <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Reemplazará el stock del {fmtDate(preview.previous.uploaded_at)} ({fmtNum(preview.previous.total_lines)} CN · {fmtNum(preview.previous.total_units)} uds).
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={confirmUpload}
              disabled={uploading}
              data-testid="stock-preview-confirm"
              className="inline-flex items-center gap-2 bg-slate-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Guardar este stock
            </button>
            <button
              onClick={() => setPreview(null)}
              className="px-5 py-2.5 border border-slate-200 rounded-md text-sm hover:bg-slate-50"
              data-testid="stock-preview-discard"
            >
              Descartar
            </button>
          </div>
        </section>
      )}

      {expiring?.has_expiry && expiring.total > 0 && (
        <section className="bg-white border border-rose-200 rounded-2xl p-6 mb-6" data-testid="stock-expiring">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-rose-700" />
            <span className="font-semibold text-sm text-rose-900">Caduca en los próximos 6 meses</span>
          </div>
          <div className="text-xs text-slate-500 mb-4">
            {fmtNum(expiring.total)} {expiring.total === 1 ? "referencia" : "referencias"} · {fmtNum(expiring.total_units)} unidades · {fmtEur(expiring.total_eur)}
            {expiring.expired > 0 && (
              <b className="text-rose-700"> · {expiring.expired} ya {expiring.expired === 1 ? "caducada" : "caducadas"}</b>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
            {expiring.items.map((it) => (
              <div key={`${it.cn}-${it.expiry}`} className="px-3 py-2 flex items-center gap-3 text-xs">
                <span className="font-mono-num text-slate-500 w-16 shrink-0">{it.cn}</span>
                <span className="flex-1 truncate text-slate-700">{it.name || "—"}</span>
                <span className="text-slate-500 hidden sm:inline truncate max-w-[140px]">{it.laboratorio}</span>
                <span className="font-mono-num">{it.stock_units} uds</span>
                <span className="font-mono-num font-semibold">{fmtEur(it.stock_eur)}</span>
                <span className={`font-mono-num font-bold ${it.days_left < 0 ? "text-rose-700" : it.days_left < 60 ? "text-amber-700" : "text-slate-500"}`}>
                  {fmtDay(it.expiry)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-sky-50/40 border border-sky-200 rounded-2xl p-5">
        <div className="inline-flex items-center gap-2 text-sky-900 mb-2">
          <Info className="h-4 w-4" /> <span className="font-semibold text-sm">¿Cómo se usa este stock?</span>
        </div>
        <ul className="text-xs text-slate-700 space-y-1 list-disc list-inside">
          <li>En el detalle de una dispensación, las moléculas <strong>Solo Marca</strong> y <strong>Marginal</strong> mostrarán si ya tienes stock del producto.</li>
          <li>Si tienes EFG de otro laboratorio distinto al elegido, se marca en color naranja con un aviso.</li>
          <li>En los pedidos (<em>Lo que nos falta</em> y <em>Reposición</em>) se descuentan automáticamente las unidades en stock.</li>
        </ul>
      </section>
    </div>
  );
}

function Flag({ ok, label, extra }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${ok ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
      <div className={`font-bold uppercase tracking-wider text-[10px] ${ok ? "text-emerald-800" : "text-slate-500"}`}>
        {ok ? "✓ " : "— "}{label}
      </div>
      <div className={`mt-0.5 ${ok ? "text-emerald-900 font-mono-num font-semibold" : "text-slate-400"}`}>{extra}</div>
    </div>
  );
}

function KpiTile({ label, value, Icon, accent = "slate" }) {
  const c = { emerald: "text-emerald-700", slate: "text-slate-900" }[accent];
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold inline-flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className={`font-display text-xl font-extrabold mt-1 ${c}`}>{value}</div>
    </div>
  );
}
